import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import Footer from '../Pages/Footer';
import toast from "react-hot-toast";
import SEO from './SEO.jsx';
import { buildProductSchema } from '../utils/structuredData';
import { API_URL } from '../config/api';

const API = API_URL;
// const API = 'http://localhost:8000';

/* --- Service pour un produit --- */
const fetchProductById = (id) =>
  fetch(`${API}/detailProduct/${id}`).then((r) => r.json());

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dispatch: cartDispatch } = useContext(CartContext);
  const { user } = useUser();

  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [rating, setRating] = useState(5);
  const [selectedImage, setSelectedImage] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
const itemsPerPage = 12; // 🔁 nombre de produits par page (modifiable)

  const transformYoutubeUrl = (url) => {
    const match = url?.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  /* ====== Charger le produit ====== */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchProductById(id);
        setProduct(data);

        const imgs = [];
        if (data.img) imgs.push(data.img);
        if (Array.isArray(data.images)) {
          data.images.forEach((img) => {
            if (img && img !== data.img) imgs.push(img);
          });
        }

        setAllImages(imgs);
        setSelectedImage(imgs[0] || null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  /* ====== Charger tous les produits ====== */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/products`);
        setAllProducts(await res.json());
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  /* ====== Produits recommandés ====== */
  useEffect(() => {
    if (!product || !allProducts.length) return;
    setRecommended(
      allProducts.filter(
        (p) => p.categorie === product.categorie && p._id !== product._id
      )
    );
  }, [product, allProducts]);

  /* ====== Commentaires ====== */
  const loadComments = async () => {
    try {
      const res = await fetch(`${API}/products/${id}/comments`);
      setComments(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadComments();
  }, [id]);

  /* ====== Soumettre un commentaire ====== */
const handleAddComment = async (e) => {
  e.preventDefault();
  if (!newComment.trim()) return;

  if (!user) {
    toast.error('Veuillez vous connecter pour laisser un commentaire.');
    navigate('/login');
    return;
  }

  try {
    const res = await fetch(`${API}/products/${id}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(user.token && { Authorization: `Bearer ${user.token}` }),
      },
      body: JSON.stringify({ 
        text: newComment,
        rating,
        userId: user._id,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json();
      toast.error(errorData.message || "Erreur lors de l'envoi du commentaire.");
      return;
    }

    toast.success("✅ Commentaire ajouté avec succès !");
    setNewComment('');
    setRating(5);
    loadComments();
  } catch (error) {
    toast.error("Erreur réseau ou serveur, réessayez plus tard.");
    console.error(error);
  }
};


const totalPages = Math.ceil(recommended.length / itemsPerPage);

const paginatedRecommended = recommended.slice(
  (currentPage - 1) * itemsPerPage,
  currentPage * itemsPerPage
);

  const getImageUrl = (img) =>
    img?.startsWith('http') ? img : `${API}/uploads/${img}`;

  if (loading) return <div>Chargement…</div>;
  if (!product) return <div>Aucun produit trouvé</div>;

  return (
    <div>
      <SEO
        title={product.title}
        description={
          product.description
            ? product.description.slice(0, 160)
            : `Achetez ${product.title} en version digitale sur CodeShop225. Téléchargement instantané et paiement sécurisé.`
        }
        path={`/product/${product._id}`}
        image={getImageUrl(product.img || product.image)}
        type="product"
        jsonLd={buildProductSchema(product, comments)}
      />
      <div className='container'>
        <nav aria-label="breadcrumb" className="breadcrumb-container">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Accueil</Link></li>
            <li className="breadcrumb-item">{product.groupe}</li>
            <li className="breadcrumb-item">{product.categorie}</li>
            <li className="breadcrumb-item active" aria-current="page">{product.title}</li>
          </ol>
        </nav>

        {/* === VISUEL & INFOS === */}
        <div className='container--article'>
          {/* Images */}
          <div className='container--article--left--part'>
            <div className='container--article--left--part--content'>
              <div className='container--thumbnail--gallery'>
                {allImages.map((img, i) => (
                  <img
                    key={i}
                    src={getImageUrl(img)}
                    alt={`thumbnail-${i}`}
                    className={`thumbnail ${selectedImage === img ? 'active' : ''}`}
                    onClick={() => setSelectedImage(img)}
                  />
                ))}
              </div>

              <Zoom>
                {selectedImage ? (
                  <img
                    src={getImageUrl(selectedImage)}
                    alt={product.title}
                    className='main-image'
                  />
                ) : null}
              </Zoom>
            </div>
          </div>

          {/* Infos produit */}
        {/* Infos produit */}
<div className="container--article--right--part improved">
  <h2 className="product-title">{product.title}</h2>

  <div className="product-meta">
    <span className="product-label">
      Marque : <strong>{product.label}</strong>
    </span>
    <span className="product-reference">
      Référence : <strong>{product.reference}</strong>
    </span>
  </div>

  <p className="product-description">{product.description}</p>

  {/* 💰 PRIX + PROMO */}
  <div className="product-price-wrapper">
    {product.promotion > 0 && product.prixOriginal > 0 ? (
      <>
        {/* Prix original barré */}
        <span className="original-price">
          {Number(product.prixOriginal).toLocaleString()} FCFA
        </span>

        {/* Prix promo */}
        <span className="product-price promo">
          {Number(product.price).toLocaleString()} FCFA
        </span>

        {/* Badge promo */}
        <span className="promo-badge">
          -{product.promotion}%
        </span>
      </>
    ) : (
      <span className="product-price">
        {Number(product.price).toLocaleString()} FCFA
      </span>
    )}
  </div>

  <button
    className="btn__add__product"
    onClick={() =>
      cartDispatch({ type: "ADD_TO_CART", payload: product })
    }
  >
    <i className="fa-solid fa-cart-shopping" /> Ajouter au panier
  </button>
</div>
        </div>

        {/* Aperçu vidéo */}
        {product.videoUrl && transformYoutubeUrl(product.videoUrl) && (
          <div className="box__youtube">
            <label style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Aperçu vidéo</label>
            <iframe
              width="100%"
              height="450"
              src={transformYoutubeUrl(product.videoUrl)}
              title="Vidéo YouTube"
              frameBorder="0"
              allowFullScreen
              style={{ borderRadius: '10px' }}
            ></iframe>
          </div>
        )}

        {/* === RECOMMANDATIONS === */}
        <section className='section__product__recommanded'>
          <h4 className='Product__recommanded__header'>Produits recommandés pour vous</h4>
          <div className='pro-grid'>
           {paginatedRecommended.map((item) => (
              <article key={item._id} className='pro-card'>
                <Link to={`/product/${item._id}`} className='pro-imgWrap'>
                  <img src={getImageUrl(item.img)} alt={item.title} />
                </Link>
                <div className='pro-info'>
                  <h4 className='pro-title'>{item.title}</h4>
                  <p className='pro-meta'>{item.label} • {item.categorie}</p>
                  <div className='pro-footer'>
                    <span className='pro-price'>
                      {Number(item.price).toLocaleString()} FCFA
                    </span>
                    <button
                      className='circle-btn'
                      onClick={() =>
                        cartDispatch({ type: 'ADD_TO_CART', payload: item })
                      }>
                      <i className='fa-solid fa-cart-plus' />
                    </button>
                  </div>
                </div>
              </article>
            ))}
            {!recommended.length && <p>Aucun produit recommandé.</p>}
          </div>

          {recommended.length > itemsPerPage && (
  <div className="pagination">
    <button
      disabled={currentPage === 1}
      onClick={() => setCurrentPage((p) => p - 1)}
    >
      ← Précédent
    </button>

    <span className='pagine'>
      Page {currentPage} / {totalPages}
    </span>

    <button
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage((p) => p + 1)}
    >
      Suivant →
    </button>
  </div>
)}
        </section>

        {/* === COMMENTAIRES === */}
<div className="product-comments">
  <h3 className="comment__tiltle">💬 Commentaires ({comments.length})</h3>

  {loading ? (
  <p className="text-gray-500 text-center py-4">Chargement des commentaires...</p>
) : comments.length === 0 ? (
  <p className="italic text-gray-400 text-center py-4">Aucun commentaire pour ce produit.</p>
) : (
  <ul className="flex flex-col gap-6">
    {comments.map((c) => {
      const userName = user?.name || user?.userName || "Invité";
      const initials = userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase();

      return (
        <li key={c._id} className="comments">
          {/* Avatar */}
          <div className="comment__initials">
            <p>{initials}</p>
          </div>

          {/* Contenu commentaire */}
          <div className="content__comment">
            <div className="content__comment__box">
               <p className="text-gray-400 text-sm">{new Date(c.createdAt).toLocaleDateString()}</p>
               <p className="comment__user__name">{userName}</p> 
                 <p className="">{c.text}</p>
                <p className="comment__note">Note : {c.rating}/10</p>
            
            </div>
          </div>
        </li>
      );
    })}
  </ul>
)}

  {/* Formulaire */}
  <form onSubmit={handleAddComment} className="mb-8 flex flex-col gap-3">
    <textarea
      className="w-full p-4 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none placeholder-gray-400"
      placeholder="Partagez votre avis sur ce produit..."
      value={newComment}
      onChange={(e) => setNewComment(e.target.value)}
      rows={4}
    />

    {/* Rating (1 à 10) */}
    <div className="flex items-center gap-2">
      <label className="text-gray-700 font-medium">Votre note :</label>
      <select value={rating}
        onChange={(e) => setRating(Number(e.target.value))}
        className="border border-gray-300 rounded-lg px-3 py-1 shadow-sm focus:ring-2 focus:ring-indigo-500"
      >
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n} / 10
          </option>
        ))}
      </select>
    </div>

    <button type="submit" className="self-end px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 shadow-md transition font-medium">
      Envoyer
    </button>
  </form>

  {/* Liste des commentaires */}
 {/* Liste des commentaires */}
</div>
      </div>

      <Footer />
      <a
        href="https://wa.me/2250101885981?text=Bonjour,%20je%20souhaite%20des%20infos%20sur%20vos%20produits"
        target="_blank" rel="noreferrer" className="whatsapp-btn">
        <i className="fab fa-whatsapp"></i>
      </a>

            <style>
        {
          `
          .product-price-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 12px 0;
}

.product-price {
  font-size: 1.6rem;
  font-weight: 700;
  color: #111;
}

.product-price.promo {
  color: #e53935;
}

.original-price {
  text-decoration: line-through;
  color: #888;
  font-size: 1.2rem;
  font-weight: bold;
}

.pagine{
color:#4b00cc
}

.promo-badge {
  background: #e53935;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
}
  .pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.pagination button {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
          `}
      </style>
    </div>
  );
};

export default ProductDetail;